import { MenuItem } from '@mui/material';
import { AxiosError } from 'axios/index';
import moment, { Moment } from 'moment/moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { ErrorResponse } from '../../../../shared/api/hscan';
import {
  Gender,
  KeycloakErrorResponseDetail,
  Locale,
} from '../../../../shared/api/hscan/account';
import {
  useChangeBirthDate,
  useChangeGender,
  useNotVerifiedChangeName,
  useNotVerifiedChangePhoneNumber,
  useUserState,
} from '../../../../shared/hooks/accountHook';
import { ISO_LOCAL_DATE } from '../../../../shared/utils/dateTime';
import birthIcon from '../../assets/icons/icon_birth.svg';
import emailIcon from '../../assets/icons/icon_email.svg';
import genderIcon from '../../assets/icons/icon_gender.svg';
import peopleIcon from '../../assets/icons/icon_people.svg';
import phoneIcon from '../../assets/icons/icon_phone.svg';
import usernameIcon from '../../assets/icons/icon_username.svg';
import { ErrorMessage } from '../../common/components/EditModal/styledComponents';
import { ColoredIcon } from '../../common/components/Icon';
import { DateFieldSlot, SelectSlot } from '../../common/components/Slot';
import { InternationalTelecomFieldSlot } from '../../common/components/Slot/InternationalTelecomSlot';
import {
  SlotGrid,
  SlotGridWrapper,
} from '../../common/components/Slot/SlotWrapper';
import { TextFieldSlot } from '../../common/components/Slot/TextFieldSlot';
import { SlotRef } from '../../common/components/Slot/types';
import { Spacer } from '../../common/components/Spacer';

import { EditLocale } from './EditLocale';
import { EditTarget } from './MyPage';
import { SaveButton, EditButton } from './styledComponent';

const UserInfoSlotGrid = styled(SlotGrid)`
  row-gap: 10px;
`;
const SlotContainer = styled.div``;
const InputErrorMessage = styled(ErrorMessage)`
  margin-top: 2px;
`;
export interface NotVerifiedUserInfoProps {
  onSuccessEdit: (editTarget: EditTarget) => void;
}

export const NotVerifiedUserInfo = (props: NotVerifiedUserInfoProps) => {
  const { onSuccessEdit } = props;
  const { t } = useTranslation();

  const [user] = useUserState();
  const [loadUserInfo, setLoadUserInfo] = useState(true);

  const nameSlotRef = useRef<SlotRef>(null);
  const birthDateSlotRef = useRef<SlotRef>(null);
  const genderSlotRef = useRef<SlotRef>(null);
  const telecomSlotRef = useRef<SlotRef>(null);

  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState<Moment | null>(null);
  const [gender, setGender] = useState<Gender | null>(null);
  const [telecom, setTelecom] = useState('');
  const [locale, setLocale] = useState<Locale | null>(null);
  const [CustomErrorMessage, setCustomErrorMessage] = useState({
    [EditTarget.Name]: '',
    [EditTarget.Phone]: '',
    [EditTarget.BirthDate]: '',
    [EditTarget.Gender]: '',
    [EditTarget.Locale]: '',
  });

  const [editingStatus, setEditingStatus] = useState({
    [EditTarget.Name]: false,
    [EditTarget.Phone]: false,
    [EditTarget.BirthDate]: false,
    [EditTarget.Gender]: false,
    [EditTarget.Locale]: false,
  });

  const { mutate: changePhoneNumber } = useNotVerifiedChangePhoneNumber({
    onSuccess: () => onSuccess(EditTarget.Phone),
    onError: (e: AxiosError<ErrorResponse<KeycloakErrorResponseDetail>>) =>
      onError(e, EditTarget.Phone),
  });

  const { mutate: changeName } = useNotVerifiedChangeName({
    onSuccess: () => onSuccess(EditTarget.Name),
    onError: (e: AxiosError<ErrorResponse<KeycloakErrorResponseDetail>>) =>
      onError(e, EditTarget.Name),
  });

  const { mutate: changeGender } = useChangeGender({
    onSuccess: () => onSuccess(EditTarget.Gender),
    onError: (e: AxiosError<ErrorResponse<KeycloakErrorResponseDetail>>) =>
      onError(e, EditTarget.Gender),
  });

  const { mutate: changeBirthDate } = useChangeBirthDate({
    onSuccess: () => onSuccess(EditTarget.BirthDate),
    onError: (e: AxiosError<ErrorResponse<KeycloakErrorResponseDetail>>) =>
      onError(e, EditTarget.BirthDate),
  });

  useEffect(() => {
    if (user && loadUserInfo) {
      setName(user.name);
      setBirthDate(moment(user.birthDate));
      setGender(user.gender);
      setTelecom(user.telecom);
      setLocale(user.locale ?? null);
    }
  }, [user, loadUserInfo]);

  const onChangeEditStatus = (key: EditTarget, value: boolean) => {
    setLoadUserInfo(false);
    setEditingStatus({ ...editingStatus, [key]: value });
  };

  const onSuccess = (key: EditTarget) => {
    setErrorMessageByKey(key, '');
    onChangeEditStatus(key, false);
    onSuccessEdit(key);
  };

  const setErrorMessageByKey = (key: EditTarget, text: string) => {
    setCustomErrorMessage({
      ...CustomErrorMessage,
      [key]: text,
    });
  };

  const onError = (
    e: AxiosError<ErrorResponse<KeycloakErrorResponseDetail>>,
    key: EditTarget,
  ) => {
    if (e.response?.status === 500) {
      setErrorMessageByKey(key, `${t('features.myPage.errorMessage.500')}`);
    } else {
      setErrorMessageByKey(
        key,
        `${t(
          `features.myPage.errorMessage.${e.response?.data.code}`,
          t('features.myPage.errorMessage.unknown'),
        )}`,
      );
    }
  };

  return (
    <SlotGridWrapper>
      <UserInfoSlotGrid $marginTop="47px" $width={450} $columnGap={56}>
        <SlotContainer>
          <TextFieldSlot
            label={`${t('features.myPage.slots.username.title')}:`}
            textAlign="center"
            value={user?.username ?? ''}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={usernameIcon}
                imageWidth="20px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            fullwidth
            readOnly>
            <Spacer width={'45px'} />
          </TextFieldSlot>
          <InputErrorMessage />
        </SlotContainer>
        <SlotContainer>
          <TextFieldSlot
            label={`${t('features.myPage.slots.email.title')}:`}
            textAlign="center"
            value={user?.email ?? ''}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={emailIcon}
                imageWidth="24px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            fullwidth
            readOnly>
            <Spacer width={'45px'} />
          </TextFieldSlot>
          <InputErrorMessage />
        </SlotContainer>
        <SlotContainer>
          <TextFieldSlot
            ref={nameSlotRef}
            label={`${t('features.myPage.slots.name.title')}:`}
            textAlign="center"
            value={name ?? ''}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={peopleIcon}
                imageWidth="18px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            onChange={setName}
            readOnly={!editingStatus[EditTarget.Name]}
            fullwidth>
            {editingStatus[EditTarget.Name] ? (
              <SaveButton
                onClick={() =>
                  name
                    ? changeName(name)
                    : setErrorMessageByKey(
                        EditTarget.Name,
                        `${t('features.myPage.errorMessage.noValue')}`,
                      )
                }>
                {t('common.button.save')}
              </SaveButton>
            ) : (
              <EditButton
                onClick={() => {
                  onChangeEditStatus(EditTarget.Name, true);
                  setTimeout(() => nameSlotRef.current?.inputs[0]?.focus(), 0);
                }}
              />
            )}
          </TextFieldSlot>
          <InputErrorMessage>
            {CustomErrorMessage[EditTarget.Name]}
          </InputErrorMessage>
        </SlotContainer>
        <SlotContainer>
          <DateFieldSlot
            ref={birthDateSlotRef}
            label={`${t('features.myPage.slots.birth.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={birthIcon}
                imageWidth="24px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            date={birthDate}
            onChangeDate={value => setBirthDate(value)}
            readOnly={!editingStatus[EditTarget.BirthDate]}>
            {editingStatus[EditTarget.BirthDate] ? (
              <SaveButton
                onClick={() =>
                  birthDate
                    ? changeBirthDate(birthDate.format(ISO_LOCAL_DATE))
                    : setErrorMessageByKey(
                        EditTarget.BirthDate,
                        `${t('features.myPage.errorMessage.noValue')}`,
                      )
                }>
                {t('common.button.save')}
              </SaveButton>
            ) : (
              <EditButton
                onClick={() => {
                  onChangeEditStatus(EditTarget.BirthDate, true);
                  setTimeout(
                    () => birthDateSlotRef.current?.inputs[0]?.focus(),
                    0,
                  );
                }}
              />
            )}
          </DateFieldSlot>
          <InputErrorMessage>
            {CustomErrorMessage[EditTarget.BirthDate]}
          </InputErrorMessage>
        </SlotContainer>
        <SlotContainer>
          <SelectSlot
            ref={genderSlotRef}
            label={`${t('features.myPage.slots.gender.title')}:`}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={genderIcon}
                imageWidth="24px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            value={gender ?? ''}
            onChange={value => {
              const gender = value as Gender;
              if (Object.values(Gender).includes(gender)) {
                setGender(gender);
              }
            }}
            readOnly={!editingStatus[EditTarget.Gender]}
            endIcon={
              editingStatus[EditTarget.Gender] ? (
                <SaveButton
                  onClick={() => gender != null && changeGender(gender)}>
                  {t('common.button.save')}
                </SaveButton>
              ) : (
                <EditButton
                  onClick={() => {
                    onChangeEditStatus(EditTarget.Gender, true);
                    setTimeout(
                      () => genderSlotRef.current?.inputs[0]?.focus(),
                      0,
                    );
                  }}
                />
              )
            }
            fullwidth>
            <MenuItem value={''}>{`${t(
              'features.myPage.gender.unselected',
            )}`}</MenuItem>
            <MenuItem value={Gender.F}>{`${t(
              'features.myPage.gender.female',
            )}`}</MenuItem>
            <MenuItem value={Gender.M}>{`${t(
              'features.myPage.gender.male',
            )}`}</MenuItem>
            <MenuItem value={Gender.O}>{`${t(
              'features.myPage.gender.others',
            )}`}</MenuItem>
          </SelectSlot>
          <InputErrorMessage>
            {CustomErrorMessage[EditTarget.Gender]}
          </InputErrorMessage>
        </SlotContainer>
        <SlotContainer>
          <InternationalTelecomFieldSlot
            ref={telecomSlotRef}
            label={`${t('features.myPage.slots.phoneNumber.title')}:`}
            value={telecom ?? ''}
            icon={
              <ColoredIcon
                width="24px"
                height="24px"
                imageUrl={phoneIcon}
                imageWidth="24px"
                color={'var(--HScan_Light_Blue, #2e5be0)'}
              />
            }
            onChange={setTelecom}
            readOnly={!editingStatus[EditTarget.Phone]}>
            {editingStatus[EditTarget.Phone] ? (
              <SaveButton
                onClick={() =>
                  telecom
                    ? changePhoneNumber(telecom)
                    : setErrorMessageByKey(
                        EditTarget.Phone,
                        `${t('features.myPage.errorMessage.noValue')}`,
                      )
                }>
                {t('common.button.save')}
              </SaveButton>
            ) : (
              <EditButton
                onClick={() => {
                  onChangeEditStatus(EditTarget.Phone, true);
                  setTimeout(
                    () => telecomSlotRef.current?.inputs[0]?.focus(),
                    0,
                  );
                }}
              />
            )}
          </InternationalTelecomFieldSlot>
          <InputErrorMessage>
            {CustomErrorMessage[EditTarget.Phone]}
          </InputErrorMessage>
        </SlotContainer>
        <EditLocale
          locale={locale}
          setLocale={setLocale}
          edit={editingStatus[EditTarget.Locale]}
          onEdit={() => onChangeEditStatus(EditTarget.Locale, true)}
          onEditSuccess={() => onSuccess(EditTarget.Locale)}
          onEditError={(
            e: AxiosError<ErrorResponse<KeycloakErrorResponseDetail>>,
          ) => onError(e, EditTarget.Locale)}
        />
        <InputErrorMessage>
          {CustomErrorMessage[EditTarget.Locale]}
        </InputErrorMessage>
      </UserInfoSlotGrid>
    </SlotGridWrapper>
  );
};
